import { useEffect, useState } from "react";
import usePageTitle from "@/hooks/usePageTitle";
import Table, { TableFooter, type Row, buildActionColumn } from "@/components/Table";
import TabLayout from "../_TabLayout";
import EmptyState from "@/components/EmptyState";
import {
  UniversitiesQuery,
  UniversityEnrollment,
  UniversityEnrollmentsQuery,
  UniversityEnrollmentsQueryVariables
} from "./types";
import Icon from "@/components/Icon";
import Avatar from "@/components/Avatar";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { useLazyQuery, useQuery } from "@apollo/client";
import { UNIVERSITIES_QUERY, UNIVERSITY_ENROLLMENTS_QUERY } from "./queries";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { useNavigate } from "@/router";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import useCapabilities from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import type { Path } from "@/router";
import { getUniversityEnrollmentCount } from "./_utils";

interface UniversityRow {
  id: string;
  legacyId: string;
  name: string;
  logoUrl?: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  totalEnrollments: number;
  alumniStudentCount: number;
}

const mapUniversityData = (data: UniversitiesQuery["universities"] | undefined) => {
  if (!data) return [];

  return data.map((university) => {
    return {
      id: university.id,
      legacyId: university.legacyId,
      name: university.name,
      logoUrl: university.logoUrl,
      address: university.address?.address1 || "",
      city: university.address?.city || "",
      state: university.address?.state || "",
      zip: university.address?.zip || "",
      totalEnrollments: getUniversityEnrollmentCount(university),
      alumniStudentCount: university.alumniStudentCount
    };
  });
};

const enrollmentColumns: ColumnDef<UniversityEnrollment, any>[] = [
  {
    accessorKey: "version",
    header: "Program Version",
    enableSorting: false
  },
  {
    accessorKey: "level0",
    header: "Level 0",
    enableSorting: false
  },
  {
    accessorKey: "level1",
    header: "Level 1",
    enableSorting: false
  },
  {
    accessorKey: "level2",
    header: "Level 2",
    enableSorting: false
  },
  {
    accessorKey: "level3",
    header: "Level 3",
    enableSorting: false
  },
  {
    accessorKey: "completed",
    header: "All Levels Complete",
    enableSorting: false
  },
  {
    accessorKey: "total",
    header: "Total",
    enableSorting: false
  }
];

const ExpandedUniversityRow = ({ universityId, isExpanded }: { universityId: string; isExpanded: boolean }) => {
  const [getEnrollments, { data, loading }] = useLazyQuery<
    UniversityEnrollmentsQuery,
    UniversityEnrollmentsQueryVariables
  >(UNIVERSITY_ENROLLMENTS_QUERY, {
    variables: { id: universityId }
  });

  useEffect(() => {
    if (isExpanded) {
      getEnrollments();
    }
  }, [isExpanded, getEnrollments]);

  const enrollments: UniversityEnrollment[] =
    data?.university?.studentProgramSummary?.map((summary) => ({
      id: summary.programVersion,
      version: summary.programVersion,
      level0: summary.activeLevel0,
      level1: summary.activeLevel1,
      level2: summary.activeLevel2,
      level3: summary.activeLevel3,
      completed: summary.completions,
      total:
        summary.activeLevel0 + summary.activeLevel1 + summary.activeLevel2 + summary.activeLevel3 + summary.completions
    })) || [];

  return <TableDetailsPanel data={enrollments} columns={enrollmentColumns} loading={loading} />;
};

export default function UniversitiesPage() {
  usePageTitle("/education/universities", "Education | PGM Universities");
  const navigate = useNavigate();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { Menu, setMenu } = useTableRowMenu();
  const { hasRole } = useCapabilities();
  const canManageUniversityStaff = hasRole(ROLE.EDUCATION_ADMIN) || hasRole(ROLE.GLOBAL_ADMIN);

  const { data: universities, loading, error } = useQuery(UNIVERSITIES_QUERY);

  if (error) {
    console.error(error);
    return (
      <TabLayout>
        <EmptyState title="Error" caption="Failed to load PGM Universities Summary." iconName="error" />
      </TabLayout>
    );
  }

  const mappedData: UniversityRow[] = mapUniversityData(universities?.universities);
  const totalActiveEnrollments = mappedData.reduce((sum, row) => sum + (row.totalEnrollments ?? 0), 0);
  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      enableSorting: true,
      size: 500,
      cell: ({ row }: { row: Row<UniversityRow> }) => (
        <div className="flex items-center gap-3">
          <Avatar picture={row.original.logoUrl || undefined} size="small" iconName="school" />
          <span className="font-medium">{row.original.name}</span>
        </div>
      )
    },
    {
      accessorKey: "address",
      header: "Address",
      enableSorting: false,
      size: 500
    },
    {
      accessorKey: "city",
      header: "City",
      enableSorting: false,
      size: 500
    },
    {
      accessorKey: "state",
      header: "State",
      enableSorting: false
    },
    {
      accessorKey: "zip",
      header: "Zip",
      enableSorting: false
    },
    {
      accessorKey: "totalEnrollments",
      header: "Enrollments",
      enableSorting: true,
      cell: ({ row }: { row: Row<UniversityRow> }) => {
        return (
          <div
            data-testid={`expand-row-${row.original.id}`}
            className="flex items-center gap-2 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              row.getToggleExpandedHandler()();
            }}
          >
            <span>{row.original.totalEnrollments ?? 0}</span>
            <Icon name={row.getIsExpanded() ? "expand_less" : "expand_more"} className="max-md:hidden" />
          </div>
        );
      },
      meta: { headerClassName: "max-sm:inline" }
    },
    {
      accessorKey: "alumniStudentCount",
      header: "Alumni",
      enableSorting: true
    },
    buildActionColumn<UniversityRow>({
      getActions: (row) => {
        if (!row.legacyId) {
          return [];
        }

        return [
          {
            text: "View University",
            onClick: () => navigate(`/education/universities/${row.legacyId}` as Path)
          },
          ...(canManageUniversityStaff
            ? [
                {
                  text: "Manage Staff",
                  onClick: () => navigate(`/education/universities/${row.legacyId}/staff` as Path)
                }
              ]
            : [])
        ];
      },
      actionId,
      setMenu
    })
  ];

  return (
    <TabLayout>
      <Menu />
      <div className="pt-12" />
      <Table
        data={mappedData}
        loading={loading}
        columns={columns}
        getRowLink={(row) => {
          const legacyId = row.original.legacyId;
          if (!legacyId) {
            return null;
          }

          return { to: `/education/universities/${legacyId}` as Path };
        }}
        renderExpandedRow={({ row }) => (
          <ExpandedUniversityRow universityId={row.original.id} isExpanded={row.getIsExpanded()} />
        )}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        renderFooter={() => (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <span className="text-label-large" data-testid="total-active-enrollments">
              Total Active Enrollment: {totalActiveEnrollments.toLocaleString()}
            </span>
            <TableFooter totalRowCount={mappedData.length} />
          </div>
        )}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search" iconName="school" />
        )}
      />
    </TabLayout>
  );
}
